import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Icons } from "./icons.jsx";
import { LiveDot } from "./primitives.jsx";
import {
  HoverVideoContainer,
  HoverVideoGradientBg,
  HoverVideoGrid,
  HoverVideoGlow,
  HoverVideoShimmer,
  HoverVideoElement,
  HoverVideoOverlay,
  HoverVideoAvatarWrap,
  HoverVideoAvatarRing,
  HoverVideoAvatarImg,
  HoverVideoPlayBadge,
  HoverVideoGuruLabel,
  HoverVideoGuruSubtitle,
  HoverVideoGuruName,
  HoverVideoLoadingBadge,
  HoverVideoPlayingBadge,
} from "./style.js";

// ── HoverVideoArt — smooth PiP ────────────────────────────────────────────────
const HoverVideoArt = ({
  program,
  hovered,
  playToken,
  onAskPlay,
  fillHeight = false,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [showVideo, setShowVideo] = useState(false);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [isTouch, setIsTouch] = useState(false);
  const [inView, setInView] = useState(false);
  const delayTimer = useRef(null);
  const [accentStart, accentEnd] = program.accent ?? ["#01ECFF", "#962EFF"];

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(hover: none)");
    const update = () => setIsTouch(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isTouch || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.6),
      { threshold: [0, 0.6, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      setInView(false);
    };
  }, [isTouch]);

  const active = isTouch ? inView : hovered;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) {
      onAskPlay?.(program.id);
      const delay = isTouch ? 1500 : 200;
      delayTimer.current = setTimeout(() => {
        setShowVideo(true);
        video.play().catch(() => {});
      }, delay);
    } else {
      clearTimeout(delayTimer.current);
      setShowVideo(false);
      try {
        video.pause();
        video.currentTime = 0;
      } catch (_) {}
    }
    return () => clearTimeout(delayTimer.current);
  }, [active, isTouch]);

  const AVATAR = 96,
    PIP_SCALE = 0.45,
    PIP_PAD = 14;
  const isPip = active && showVideo;
  const { width, height } = dims;
  const aLeft =
    width > 0 ? (isPip ? width - PIP_PAD - AVATAR * PIP_SCALE : width / 2 - AVATAR / 2) : 0;
  const aTop =
    height > 0 ? (isPip ? height - PIP_PAD - AVATAR * PIP_SCALE : height / 2 - AVATAR / 2) : 0;
  const aSize = isPip ? AVATAR * PIP_SCALE : AVATAR;

  return (
    <HoverVideoContainer ref={containerRef} $fillHeight={fillHeight}>
      <HoverVideoGradientBg $a={accentStart} $b={accentEnd} />
      <HoverVideoGrid />
      <HoverVideoGlow $a={accentStart} />
      {!active && <HoverVideoShimmer />}
      <HoverVideoElement
        ref={videoRef}
        playsInline
        loop
        preload="metadata"
        src={program.intro_video_url}
        $showVideo={showVideo}
      />
      <HoverVideoOverlay $showVideo={showVideo} />
      {width > 0 && (
        <HoverVideoAvatarWrap $left={aLeft} $top={aTop} $size={aSize}>
          <HoverVideoAvatarRing $a={accentStart} $b={accentEnd} $isPip={isPip} />
          <HoverVideoAvatarImg
            $src={program.guru?.photo}
            $isPip={isPip}
          />
          {!isPip && (
            <HoverVideoPlayBadge $a={accentStart} $b={accentEnd}>
              <Icons.play size={14} style={{ color: "#fff" }} />
            </HoverVideoPlayBadge>
          )}
        </HoverVideoAvatarWrap>
      )}
      <HoverVideoGuruLabel $hovered={active}>
        <HoverVideoGuruSubtitle>Your Guru</HoverVideoGuruSubtitle>
        <HoverVideoGuruName>
          {program.guru?.full_name?.split(" ")[0]}
        </HoverVideoGuruName>
      </HoverVideoGuruLabel>
      {!showVideo && active && (
        <HoverVideoLoadingBadge>
          <Icons.play size={12} /> <span>Loading intro…</span>
        </HoverVideoLoadingBadge>
      )}
      {showVideo && (
        <HoverVideoPlayingBadge>
          <LiveDot />{" "}
          <span>{program.guru?.full_name?.split(" ")[0]}'s Introduction Video</span>
        </HoverVideoPlayingBadge>
      )}
    </HoverVideoContainer>
  );
};

export default HoverVideoArt;
